import { Button } from "@/components/ui/button";
import HomeClientsSection from "@/components/HomeClientsSection";
import ParticleCanvas from "@/components/ParticleCanvas";
import { useEffect, useRef, useState } from "react";


interface HomeHeroSectionProps {
  onRequestAudit: () => void;
}

const HomeHeroSection = ({ onRequestAudit }: HomeHeroSectionProps) => {
  return (
    <section className="relative md:min-h-[90vh] flex flex-col md:items-center overflow-hidden pt-32 md:pt-48 pb-12 md:pb-[80px]">
      <ParticleCanvas />

      {/* Content */}
      <div className="container mx-auto lg:px-8 relative z-10 px-4">
        <div className="flex flex-col md:flex-row items-start gap-10 md:gap-8">
          {/* Left column - 60% */}
          <div className="w-full md:w-[60%] text-left">
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light tracking-tight mb-6">
              Secure the code
              <br />
              behind your <span className="text-gradient-red font-normal">protocol</span>
            </h1>

            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mb-10">
              One mistake can cost you everything.
              <br />
              We find critical vulnerabilities
              <br />
              <span className="text-primary font-semibold">before hackers do</span>.
            </p>

            <div className="flex flex-col-reverse sm:flex-row items-start gap-4">
              <Button
                variant="outline"
                size="lg"
                className="btn-primary-outline min-w-[200px] rounded-full px-8 py-6 text-base hover:bg-primary hover:text-primary-foreground hover:border-primary"
                asChild
              >
                <a href="https://github.com/bugblow/audits" target="_blank" rel="noopener noreferrer">
                  Previous projects
                </a>
              </Button>
              <Button
                size="lg"
                className="btn-primary-filled rounded-full min-w-[240px] px-10 py-7 text-lg sm:min-w-[200px] sm:px-8 sm:py-6 sm:text-base glow-red"
                onClick={onRequestAudit}
              >
                Request an audit
              </Button>
            </div>

            {/* Social links - mobile */}
            <div className="flex xl:hidden items-center gap-4 mt-8">
              <SocialLink href="https://x.com/BugBlow" icon="x" />
              <SocialLink href="https://www.linkedin.com/in/alexander-dolgavin-au/" icon="linkedin" />
              <SocialLink href="https://www.youtube.com/@alexdolgavin" icon="youtube" />
              <SocialLink href="https://t.me/alexdesuwu" icon="telegram" />
              <SocialLink href="https://github.com/BugBlow/audits" icon="github" />
            </div>
          </div>

          {/* Right column - 40% stats */}
          <div className="w-full md:w-[40%] flex flex-col gap-0 md:items-end mt-4 md:mt-8">
            <StatItem target={23} label="AUDITS IN 2025" />
            <StatDivider />
            <StatItem target={136} label="VULNERABILITIES FOUND" isAccentLabel />
            <StatDivider />
            <StatItem target={20} prefix="$" suffix="M+" label="VALUE SECURED" isSecured />
          </div>
        </div>
      </div>

      {/* Social links - desktop (only on xl+ where there's room beside stats) */}
      <div className="absolute right-4 top-1/2 -translate-y-1/2 hidden xl:flex flex-col gap-4 z-20">
        <SocialLink href="https://x.com/BugBlow" icon="x" />
        <SocialLink href="https://www.linkedin.com/in/alexander-dolgavin-au/" icon="linkedin" />
        <SocialLink href="https://www.youtube.com/@alexdolgavin" icon="youtube" />
        <SocialLink href="https://t.me/alexdesuwu" icon="telegram" />
        <SocialLink href="https://github.com/BugBlow/audits" icon="github" />
      </div>

      {/* Clients marquee - absolute on desktop, in-flow on mobile */}
      <div className="md:absolute md:bottom-0 md:left-0 md:right-0 z-10 w-full max-w-full overflow-hidden mt-12 md:mt-0">
        <HomeClientsSection />
      </div>
    </section>
  );
};

const useCountUp = (target: number, duration = 1500) => {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setStarted(true); observer.disconnect(); } },
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!started) return;
    const start = performance.now();
    const tick = (now: number) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.round(eased * target));
      if (progress < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [started, target, duration]);

  return { count, ref };
};

const StatDivider = () => (
  <div className="w-full md:w-[220px] h-px my-8" style={{ background: 'rgba(255,255,255,0.06)' }} />
);

const StatItem = ({ target, label, prefix = "", suffix = "", isSecured = false, isAccentLabel = false }: {
  target: number; label: string; prefix?: string; suffix?: string; isSecured?: boolean; isAccentLabel?: boolean;
}) => {
  const { count, ref } = useCountUp(target);
  const labelClassName = isSecured ? 'text-[#22c55e]' : isAccentLabel ? 'text-primary' : 'text-muted-foreground/50';

  return (
    <div ref={ref} className="text-right">
      <div className="text-5xl font-bold text-foreground tracking-tight">
        {prefix}{count}{suffix}
      </div>
      <div className="text-xs uppercase tracking-wider mt-1.5 flex items-center justify-end gap-1.5">
        {isSecured && <span className="text-[#22c55e]">●</span>}
        <span className={labelClassName}>{label}</span>
      </div>
    </div>
  );
};

const socialLabels: Record<string, string> = {
  x: "X (Twitter)",
  linkedin: "LinkedIn",
  youtube: "YouTube",
  telegram: "Telegram",
  github: "GitHub",
};

const SocialLink = ({ href, icon }: { href: string; icon: string }) => {
  const icons: Record<string, JSX.Element> = {
    x: (
      <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
      </svg>
    ),
    linkedin: (
      <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
      </svg>
    ),
    youtube: (
      <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
      </svg>
    ),
    telegram: (
      <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
        <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
      </svg>
    ),
    github: (
      <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
        <path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12" />
      </svg>
    ),
  };

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="w-10 h-10 flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors duration-200"
      aria-label={socialLabels[icon] || icon}
    >
      {icons[icon]}
    </a>
  );
};

export default HomeHeroSection;
